import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Grid, TextField, Button, Box, Avatar, MenuItem, Divider, Alert
} from '@mui/material';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// The year ends most companies here actually use. Anything else is set with the month and
// day fields below, so the presets are a shortcut rather than a limit.
const YEAR_END_PRESETS = [
  { label: '31 December (calendar year)', month: 12, day: 31 },
  { label: '30 June', month: 6, day: 30 },
  { label: '31 March', month: 3, day: 31 },
  { label: '30 September', month: 9, day: 30 }
];

const DEPRECIATION_METHODS = [
  { value: 'straight_line', label: 'Straight line' },
  { value: 'reducing_balance', label: 'Reducing balance' }
];

const CompanyInfo = () => {
  const [form, setForm] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    fiscalYearEndMonth: 12,
    fiscalYearEndDay: 31,
    defaultDepreciationMethod: 'straight_line',
    payoutReminderLeadDays: 3,
    payoutReminderRecipients: '',
  });
  const [logoUrl, setLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const keys = [
    'companyName','companyEmail','companyPhone','companyAddress','companyLogo',
    'fiscalYearEndMonth','fiscalYearEndDay','defaultDepreciationMethod',
    'payoutReminderLeadDays','payoutReminderRecipients',
    'autoSendInvoices', 'autoGenerateInvoices'
  ];

  // February is capped at 28 on purpose: a 29 February year end would exist only every
  // fourth year, shifting every comparative period with it.
  const daysInMonth = (m) => [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1] || 31;

  const matchedPreset = YEAR_END_PRESETS.findIndex(
    (p) => p.month === Number(form.fiscalYearEndMonth) && p.day === Number(form.fiscalYearEndDay)
  );

  const applyPreset = (index) => {
    const preset = YEAR_END_PRESETS[index];
    if (preset) setForm({ ...form, fiscalYearEndMonth: preset.month, fiscalYearEndDay: preset.day });
  };

  const setYearEndMonth = (month) => {
    const m = Number(month);
    // Clamp the day so switching to a shorter month cannot leave an impossible date behind.
    setForm({
      ...form,
      fiscalYearEndMonth: m,
      fiscalYearEndDay: Math.min(Number(form.fiscalYearEndDay) || 1, daysInMonth(m))
    });
  };

  useEffect(() => {
    fetchSettings();
    // On mount only; fetchSettings is declared below and reads nothing that changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const results = await Promise.all(keys.map(k => axios.get(`/settings/${k}`)));
      const map = Object.fromEntries(keys.map((k,i)=>[k, results[i].data.value || '' ]));
      // Read from the raw response, not from `map`: the `|| ''` above turns a stored
      // `false` into an empty string. It would still render as off, but only because ''
      // happens to be falsy — and a switch whose correctness rests on that is one edit away
      // from silently inverting. Unset means ON, which is what the system did before this
      // setting existed.
      // Read from the raw response, not from `map`: the `|| ''` above turns a stored
      // `false` into an empty string. It would still render as off, but only because ''
      // happens to be falsy — and a switch whose correctness rests on that is one edit away
      // from silently inverting. Unset means ON, which is what the system did before these
      // settings existed.
      const asToggle = (raw) => (
        (raw === null || raw === undefined || raw === '')
          ? true
          : (typeof raw === 'string' ? raw.toLowerCase() !== 'false' : Boolean(raw))
      );
      const autoSendInvoices = asToggle(results[keys.indexOf('autoSendInvoices')].data.value);
      const autoGenerateInvoices = asToggle(results[keys.indexOf('autoGenerateInvoices')].data.value);
      setForm({
        companyName: map.companyName,
        companyEmail: map.companyEmail,
        companyPhone: map.companyPhone,
        companyAddress: map.companyAddress,
        fiscalYearEndMonth: Number(map.fiscalYearEndMonth) || 12,
        fiscalYearEndDay: Number(map.fiscalYearEndDay) || 31,
        defaultDepreciationMethod: map.defaultDepreciationMethod || 'straight_line',
        payoutReminderLeadDays: Number(map.payoutReminderLeadDays) || 3,
        payoutReminderRecipients: map.payoutReminderRecipients || '',
        autoSendInvoices,
        autoGenerateInvoices,
      });
      setLogoUrl(map.companyLogo ? `${axios.defaults.baseURL.replace('/api', '')}${map.companyLogo}` : '/logo.png');
    } catch (e) { /* ignore initial empty */ }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!/image\/png|image\/jpeg/.test(file.type)) { toast.error('Only PNG or JPG allowed'); return; }
    if (file.size > 2*1024*1024) { toast.error('Logo must be <= 2MB'); return; }
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('logo', file);
      const res = await axios.post('/uploads/company-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const path = res.data.logoFile.filePath;
      await axios.put('/settings/companyLogo', { value: path });
      setLogoUrl(`${axios.defaults.baseURL.replace('/api', '')}${path}`);
      toast.success('Logo updated');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to upload logo');
    } finally { setUploading(false); }
  };

  const save = async () => {
    try {
      setSaving(true);
      await Promise.all([
        axios.put('/settings/companyName', { value: form.companyName }),
        axios.put('/settings/companyEmail', { value: form.companyEmail }),
        axios.put('/settings/companyPhone', { value: form.companyPhone }),
        axios.put('/settings/companyAddress', { value: form.companyAddress }),
        axios.put('/settings/fiscalYearEndMonth', { value: Number(form.fiscalYearEndMonth) }),
        axios.put('/settings/fiscalYearEndDay', { value: Number(form.fiscalYearEndDay) }),
        axios.put('/settings/defaultDepreciationMethod', { value: form.defaultDepreciationMethod }),
        axios.put('/settings/payoutReminderLeadDays', { value: Number(form.payoutReminderLeadDays) }),
        axios.put('/settings/payoutReminderRecipients', { value: form.payoutReminderRecipients }),
        axios.put('/settings/autoSendInvoices', { value: Boolean(form.autoSendInvoices) }),
        axios.put('/settings/autoGenerateInvoices', { value: Boolean(form.autoGenerateInvoices) }),
      ]);
      toast.success('Company information saved');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save company information');
    }
    finally { setSaving(false); }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Company Information</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Company Name" value={form.companyName}
                onChange={e=>setForm({ ...form, companyName: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="email" label="Company Email" value={form.companyEmail}
                onChange={e=>setForm({ ...form, companyEmail: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Company Phone" value={form.companyPhone}
                onChange={e=>setForm({ ...form, companyPhone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Company Address" value={form.companyAddress}
                onChange={e=>setForm({ ...form, companyAddress: e.target.value })} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Accounting Policy</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            The financial year end decides the period every report covers and the point at
            which the books are closed into retained earnings. Changing it moves those
            boundaries — set it once, before closing a year.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth label="Financial Year End"
                value={matchedPreset === -1 ? 'custom' : matchedPreset}
                onChange={(e) => applyPreset(Number(e.target.value))}
                helperText="Pick a common year end, or choose Custom and set the date below."
              >
                {YEAR_END_PRESETS.map((p, i) => (
                  <MenuItem key={p.label} value={i}>{p.label}</MenuItem>
                ))}
                <MenuItem value="custom">Custom date</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                select fullWidth label="Year End Month"
                value={form.fiscalYearEndMonth}
                onChange={(e) => setYearEndMonth(e.target.value)}
              >
                {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                select fullWidth label="Year End Day"
                value={form.fiscalYearEndDay}
                onChange={(e) => setForm({ ...form, fiscalYearEndDay: Number(e.target.value) })}
              >
                {Array.from({ length: daysInMonth(Number(form.fiscalYearEndMonth)) }, (_, i) => i + 1)
                  .map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth label="Default Depreciation Method"
                value={form.defaultDepreciationMethod}
                onChange={(e) => setForm({ ...form, defaultDepreciationMethod: e.target.value })}
                helperText="Applied to new assets. Each asset can override it."
              >
                {DEPRECIATION_METHODS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Depreciation Runs" value="Monthly" disabled
                helperText="A twelfth of the annual charge each month, so interim reports stay accurate."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Raising Rent Invoices</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Controls whether the nightly run raises each tenant&apos;s rent invoice on their
            own pay day. Turning it off stops that run only — staff can still raise invoices
            by hand, and a tenant paying for months ahead still gets those months invoiced so
            their money has something to settle against.
          </Alert>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.autoGenerateInvoices)}
                onChange={(e) => setForm({ ...form, autoGenerateInvoices: e.target.checked })}
              />
            }
            label={form.autoGenerateInvoices
              ? 'On — rent invoices are raised automatically each month'
              : 'Off — no rent invoices are raised; you will need to raise them yourself'}
          />
          {!form.autoGenerateInvoices && (
            /* Said plainly, because nothing else will say it. The absence of an invoice is
               invisible: no error, no screen, just a month where nobody was billed. */
            <Alert severity="warning" sx={{ mt: 1 }}>
              While this is off, no tenant is billed by the system. Rent still accrues under
              the tenancy, but nothing appears on their account until an invoice is raised.
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Sending Invoices to Tenants</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Controls whether the system tells tenants about their invoices on its own — when
            an invoice is issued, and when the nightly rent run raises one. Invoices are
            still raised and posted to the ledger either way; only the message to the tenant
            is affected.
          </Alert>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.autoSendInvoices)}
                onChange={(e) => setForm({ ...form, autoSendInvoices: e.target.checked })}
              />
            }
            label={form.autoSendInvoices
              ? 'On — tenants are sent their invoices automatically'
              : 'Off — tenants are not told; send each invoice yourself from the invoice list'}
          />
          {!form.autoSendInvoices && (
            /* Said plainly, because the consequence is invisible otherwise: invoices keep
               being raised and nobody hears about them. The invoice list marks which have
               been sent, and carries a Send button for the rest. */
            <Alert severity="warning" sx={{ mt: 1 }}>
              While this is off, tenants receive nothing automatically. Invoices still appear
              in the invoice list, where unsent ones are marked and can be sent individually.
            </Alert>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>Landlord Payout Reminders</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Sent each morning to whoever pays the landlords — not to the landlords themselves.
            Each property gets one reminder per stage per month: ahead of its pay day, on the
            day, and once if it goes past unpaid.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth type="number" label="Remind This Many Days Ahead"
                inputProps={{ min: 0, max: 30 }}
                value={form.payoutReminderLeadDays}
                onChange={(e) => setForm({ ...form, payoutReminderLeadDays: e.target.value })}
                helperText="0 means only remind on the day itself."
              />
            </Grid>
            <Grid item xs={12} md={8}>
              {/* Recipients moved to Admin → Reminder Routing, where a user group is chosen
                  and the address is read from the user record. Left as a pointer rather than
                  removed silently: an admin who came here to change who gets reminded needs
                  telling where that lives now, or they will conclude the setting is gone. */}
              <Alert severity="info" sx={{ height: '100%' }}>
                Recipients are set under <strong>Reminder Routing</strong>, by user group
                rather than by email address — so changing someone&apos;s email in User
                Management is the only change needed.
                {form.payoutReminderRecipients ? (
                  <Box component="span" sx={{ display: 'block', mt: 1 }}>
                    Still sending to <strong>{form.payoutReminderRecipients}</strong> from the
                    old settings. Subscribe those people under Reminder Routing, then clear
                    this below.
                  </Box>
                ) : null}
              </Alert>
            </Grid>
            {form.payoutReminderRecipients ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Legacy recipient addresses"
                  value={form.payoutReminderRecipients}
                  onChange={(e) => setForm({ ...form, payoutReminderRecipients: e.target.value })}
                  helperText={
                    'Kept only so reminders configured before routing existed keep arriving. '
                    + 'Clear this once the same people are subscribed under Reminder Routing.'
                  }
                />
              </Grid>
            ) : null}
          </Grid>

          <Box mt={3}>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle1" gutterBottom>Company Logo (PNG/JPG, max 2MB)</Typography>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="Company Logo"
                sx={{
                  width: 140,
                  height: 140,
                  objectFit: 'contain',
                  border: '1px solid #eee',
                  borderRadius: 1,
                  bgcolor: '#f5f5f5',
                  p: 1
                }}
              />
            ) : (
              <Avatar sx={{ width: 140, height: 140, border: '1px solid #eee', bgcolor: '#e0e0e0' }}>
                No Logo
              </Avatar>
            )}
            <Button component="label" variant="outlined" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload Logo'}
              <input type="file" hidden accept="image/png,image/jpeg" onChange={e=>uploadLogo(e.target.files?.[0])} />
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CompanyInfo;
